/**
 * Displays the enseigne (retail brand) logo as a small image.
 * Falls back to a colored initial bubble if the logo fails to load.
 *
 * Usage: <EnseigneLogo enseigne="Leclerc" size={40} />
 */
import { useState } from "react";

// Locally-hosted brand logos (highest priority).
// Fallback: icon.horse → Google favicons → colored initial bubble.
const LOCAL_LOGO = {
  "leclerc":            "/logos/leclerc.png",
  "e.leclerc":          "/logos/leclerc.png",
  "carrefour":          "/logos/carrefour.png",
  "carrefour city":     "/logos/carrefour.png",
  "carrefour market":   "/logos/carrefour.png",
  "carrefour contact":  "/logos/carrefour.png",
  "carrefour express":  "/logos/carrefour.png",
  "intermarché":        "/logos/intermarche.png",
  "intermarche":        "/logos/intermarche.png",
  "systeme u":          "/logos/systeme-u.png",
  "système u":          "/logos/systeme-u.png",
  "super u":            "/logos/systeme-u.png",
  "hyper u":            "/logos/systeme-u.png",
  "u express":          "/logos/systeme-u.png",
};

const DOMAIN = {
  "auchan":             "auchan.fr",
  "casino":             "casino.fr",
  "geant casino":       "geantcasino.fr",
  "monoprix":           "monoprix.fr",
  "franprix":           "franprix.fr",
  "cora":               "cora.fr",
  "lidl":               "lidl.fr",
  "aldi":               "aldi.fr",
  "netto":              "netto.fr",
};

const primarySrc  = (d) => `https://icon.horse/icon/${d}`;
const fallbackSrc = (d) => `https://www.google.com/s2/favicons?domain=${d}&sz=128`;

const BG_FALLBACK = {
  "L": "#0064b1", "C": "#003d7a", "I": "#e2001a", "A": "#e2000f",
  "S": "#e40017", "M": "#f7b100", "F": "#d80026", "H": "#e40017",
};

const normalize = (s) => (s || "").toLowerCase().trim();
const initials = (s) => (s || "?").slice(0, 1).toUpperCase();

export default function EnseigneLogo({ enseigne, logoUrl, size = 36, rounded = "md", className = "" }) {
  const [step, setStep] = useState(0); // 0=custom/local, 1=primary, 2=fallback, 3=bubble
  const key = normalize(enseigne);
  const localSrc = logoUrl || LOCAL_LOGO[key] || null;
  const dom = DOMAIN[key] || null;
  const roundedCls = rounded === "full" ? "rounded-full" : rounded === "lg" ? "rounded-lg" : "rounded-md";
  const init = initials(enseigne);
  const bg = BG_FALLBACK[init] || "#64748B";

  let src = null;
  if (step === 0 && localSrc) src = localSrc;
  else if (step <= 1 && dom) src = primarySrc(dom);
  else if (step <= 2 && dom) src = fallbackSrc(dom);

  if (src) {
    return (
      <img
        key={`${enseigne}-${step}`}
        src={src}
        alt={enseigne || "enseigne"}
        onError={() => setStep(step + 1)}
        loading="lazy"
        referrerPolicy="no-referrer"
        className={`${roundedCls} object-contain bg-white border border-slate-100 shrink-0 ${className}`}
        style={{ width: size, height: size, padding: 3 }}
        title={enseigne}
        data-testid="enseigne-logo"
      />
    );
  }
  return (
    <div
      className={`${roundedCls} grid place-items-center text-white font-bold shrink-0 ${className}`}
      style={{ width: size, height: size, background: bg, fontSize: Math.floor(size * 0.42) }}
      title={enseigne}
      data-testid="enseigne-logo-fallback"
    >{init}</div>
  );
}
